import { html, TemplateResult, CSSResultGroup } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import componentStyles from './sic-microsite-menu.css.lit';
import { OutlineElement } from '../../base/outline-element/outline-element';

/**
 * The SIC Microsite Menu
 *
 * @element sic-microsite-menu
 * @slot default slot unordered list of menu items
 *
 * Lightdom styles in sic-microsite-menu.lightdom.css included in /shared.css
 */
@customElement('sic-microsite-menu')
export class SicMicrositeMenu extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];

  /**
   * The aria label of the nav
   *
   * @param navigationLabel
   * @default 'Main Navigation'
   */
  @property({ type: String, reflect: true, attribute: 'navigation-label' })
  navigationLabel = 'Main Navigation';

  private outsideClickListener: ((e: MouseEvent) => void) | null = null;

  firstUpdated(): void {
    this.addButtonEventListeners();
    this.handleOutsideClickListener =
      this.handleOutsideClickListener.bind(this);
    this.handleOutsideClickListener(true);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.handleOutsideClickListener(false);
  }

  render(): TemplateResult {
    return html`
      <nav aria-label="${this.navigationLabel}"><slot></slot></nav>
    `;
  }

  // A list of the top buttons for the menu.
  protected topListItems: NodeListOf<Element>;

  addButtonEventListeners() {
    window.addEventListener('keydown', this.handleToggle.bind(this));

    this.topListItems = this.querySelectorAll('button.dropdown-trigger');

    this.topListItems?.forEach(el => {
      el?.addEventListener('click', this.handleToggle.bind(this));
    });
  }

  handleOutsideClickListener(set: boolean) {
    if (set && !this.outsideClickListener) {
      this.outsideClickListener = (e: MouseEvent) => {
        const target = e.target as Node;
        if (!this.contains(target)) {
          this.setExpandedState(null, false);
        }
      };
      document.addEventListener('click', this.outsideClickListener);
      return;
    }

    if (this.outsideClickListener) {
      document.removeEventListener('click', this.outsideClickListener);
      return;
    }
  }

  handleToggle(e: Event) {
    if (e.type === 'keydown') {
      const keystroke = e as KeyboardEvent;
      if (keystroke.key !== 'Escape') {
        return;
      } else {
        this.setExpandedState(null, false);
        return;
      }
    }

    const el = e.target as HTMLElement;
    const state = el.getAttribute('aria-expanded');

    if (state === 'false') {
      this.setExpandedState(el, true);
    } else {
      this.setExpandedState(el, false);
    }
  }

  setExpandedState(el: HTMLElement | null, state: boolean) {
    el?.setAttribute('aria-expanded', state.toString());
    this.topListItems.forEach(element => {
      if (el !== element) {
        element.setAttribute('aria-expanded', 'false');
      }
    });
  }
}
declare global {
  interface HTMLElementTagNameMap {
    'sic-microsite-menu': SicMicrositeMenu;
  }
}
